/**
 * Character mapping aligned with the historical `i18next-pseudo` defaults so
 * visual stress-testing stays familiar for developers who used that plugin.
 */
export const UGLIFIED_ALPHABET: Readonly<Record<string, string>> = {
  a: 'α',
  b: 'ḅ',
  c: 'ͼ',
  d: 'ḍ',
  e: 'ḛ',
  f: 'ϝ',
  g: 'ḡ',
  h: 'ḥ',
  i: 'ḭ',
  j: 'ĵ',
  k: 'ḳ',
  l: 'ḽ',
  m: 'ṃ',
  n: 'ṇ',
  o: 'ṓ',
  p: 'ṗ',
  q: 'ʠ',
  r: 'ṛ',
  s: 'ṡ',
  t: 'ţ',
  u: 'ṵ',
  v: 'ṽ',
  w: 'ẁ',
  x: 'ẋ',
  y: 'ẏ',
  z: 'ẓ',
  A: 'À',
  B: 'β',
  C: 'Ḉ',
  D: 'Ḍ',
  E: 'Ḛ',
  F: 'Ḟ',
  G: 'Ḡ',
  H: 'Ḥ',
  I: 'Ḭ',
  J: 'Ĵ',
  K: 'Ḱ',
  L: 'Ḻ',
  M: 'Ṁ',
  N: 'Ṅ',
  O: 'Ṏ',
  P: 'Ṕ',
  Q: 'Ǫ',
  R: 'Ṛ',
  S: 'Ṣ',
  T: 'Ṫ',
  U: 'Ṳ',
  V: 'Ṿ',
  W: 'Ŵ',
  X: 'Ẋ',
  Y: 'Ŷ',
  Z: 'Ż',
} as const;

/** Letters that receive lengthening via repeated mapped glyphs (default ×2). */
export const PSEUDO_VOWELS = [
  'a',
  'e',
  'i',
  'o',
  'u',
  'y',
  'A',
  'E',
  'I',
  'O',
  'U',
  'Y',
] as const;

/**
 * Length thresholds that drive the adaptive `letterMultiplier`.
 * Short strings expand more aggressively; long strings expand conservatively
 * so they don't overflow containers that already have plenty of copy.
 *
 * | String length  | Multiplier |
 * | -------------- | ---------- |
 * | ≤ 10 chars     | 3          |
 * | 11 – 30 chars  | 2          |
 * | > 30 chars     | 1          |
 */
export const PSEUDO_LENGTH_THRESHOLDS = [
  { maxLength: 10, multiplier: 3 },
  { maxLength: 30, multiplier: 2 },
] as const;

/**
 * Returns the adaptive vowel multiplier for `value`.
 * Callers can override by passing an explicit `letterMultiplier` option.
 */
export const deriveLetterMultiplier = (length: number): number => {
  for (const { maxLength, multiplier } of PSEUDO_LENGTH_THRESHOLDS) {
    if (length <= maxLength) return multiplier;
  }
  return 1;
};

export type PseudoTransformOptions = {
  /**
   * Repeat count for vowel mapped glyphs.
   * When omitted, derived automatically from the string length via
   * {@link deriveLetterMultiplier}: shorter strings expand more (3×),
   * medium strings expand at the historical default (2×), and long
   * strings expand minimally (1×).
   */
  letterMultiplier?: number;
  /** Letters that use `letterMultiplier`. Defaults to {@link PSEUDO_VOWELS}. */
  repeatedLetters?: readonly string[];
  /** Homoglyph table. Defaults to {@link UGLIFIED_ALPHABET}. */
  letters?: Readonly<Record<string, string>>;
  /** Wrap entire string in `[` `]` to spot truncation. Default `false`. */
  wrapped?: boolean;
};

/**
 * Pseudolocalizes a string using the same rules as `i18next-pseudo`: map Latin
 * letters to accented / homoglyph forms, stretch vowels, and skip characters
 * inside `{{ … }}` interpolation spans (brace-depth heuristic).
 *
 * The vowel multiplier adapts to the length of the input when not explicitly
 * overridden — shorter strings receive a larger multiplier to better expose
 * truncation issues, while longer strings use a smaller multiplier to avoid
 * excessive overflow.
 *
 * @param value - Resolved translation (usually after i18next interpolation).
 * @param options - Optional overrides for QA tuning.
 * @returns Transformed string, or the original when `value` is empty.
 */
export const pseudoTransformString = (
  value: string,
  options: PseudoTransformOptions = {}
): string => {
  if (!value) return value;

  const letterMultiplier =
    options.letterMultiplier ?? deriveLetterMultiplier(value.length);
  const repeatedLetters = options.repeatedLetters ?? PSEUDO_VOWELS;
  const letters = options.letters ?? UGLIFIED_ALPHABET;
  const wrapped = options.wrapped ?? false;

  let braceDepth = 0;
  const processed = [...value]
    .map((letter) => {
      if (letter === '}') {
        braceDepth = 0;
        return letter;
      }
      if (letter === '{') {
        braceDepth++;
        return letter;
      }
      if (braceDepth === 2) return letter;

      if (repeatedLetters.includes(letter)) {
        const mapped = letters[letter];
        return mapped ? mapped.repeat(letterMultiplier) : letter;
      }

      return letters[letter] ?? letter;
    })
    .join('');

  return wrapped ? `[${processed}]` : processed;
};
